import axios, { AxiosError, AxiosRequestConfig, AxiosResponse, AxiosStatic } from 'axios';
import pako from 'pako';
import guid from 'uuid';

import { AxiosMethod, ContentTypes, ResponseStatus } from 'const';
import { isMethod } from 'utils/isMethod';
import { uploadFile } from './api';

const promiseMethods = [AxiosMethod.POST, AxiosMethod.GET, AxiosMethod.PUT];
// for the methods getUri, request, post, get, put, delete, etc. the config parameter is the last one
const configParameterIndexes: Record<AxiosMethod, number> = {
  [AxiosMethod.GET]: 1,
  [AxiosMethod.POST]: 2,
  [AxiosMethod.PUT]: 2,
};
const bodyParameterIndexes: Record<AxiosMethod, number | null> = {
  [AxiosMethod.GET]: null,
  [AxiosMethod.POST]: 1,
  [AxiosMethod.PUT]: 1,
};

const delay = ms => new Promise(res => setTimeout(res, ms));

// this WS API used due to API Gateway integration timeout 29 sec (backend operations are could be too long)
// https://docs.aws.amazon.com/apigateway/latest/developerguide/limits.html
// backend lambda functions executes twice recursively
// on the first run lambda close API Gateway and invokes itself the second time
// on the second run lambda makes a call to REST API and sends the response back via WS
export const axiosWS = new Proxy(axios, {
  get(target, propKey, receiver) {
    if (!isMethod(target, propKey)) {
      return Reflect.get(target, propKey, receiver);
    }

    return new Proxy(target[propKey], {
      async apply(applyTarget: AxiosStatic[typeof propKey], thisArg: typeof target, args: Parameters<typeof applyTarget>) {
        if (!promiseMethods.includes(propKey as AxiosMethod)) {
          return Reflect.apply(applyTarget, thisArg, args);
        }

        const parameterIndex = configParameterIndexes[propKey as AxiosMethod];
        const config: AxiosRequestConfig = args[parameterIndex] = args[parameterIndex] || {};

        if (!config.headers) {
          config.headers = {};
        }

        // upload request body to S3 due to invocation payload lambda limit 6mb
        // https://docs.aws.amazon.com/lambda/latest/dg/gettingstarted-limits.html

        const bodyArgIndex = bodyParameterIndexes[propKey as AxiosMethod];
        if (bodyArgIndex !== null) {
          const body = args[bodyArgIndex];
          if (body) {
            const compressedBodyUintArray = pako.gzip(JSON.stringify(body));
            const bodyFile = new File(
              [compressedBodyUintArray],
              `${guid()}.gzip`,
              { type: ContentTypes.JSON },
            );

            const bodyS3Key = await uploadFile(bodyFile, ContentTypes.JSON, {
              'Content-Encoding': 'gzip',
            });

            config.headers['x-s3-body-key'] = bodyS3Key;

            args[bodyArgIndex] = {};
          }
        }

        const response: AxiosResponse = await Reflect.apply(applyTarget, thisArg, args);
        const { responseUri, retryAttemtps, retryTimeoutSeconds, responseStatus, error } = response.data;

        if (responseStatus === ResponseStatus.FAILURE) {
          const { type, message } = error;

          return Promise.reject({ type, message });
        }

        if (!responseUri) {
          return response;
        }

        for (let i = 0; i < retryAttemtps; i++) {
          try {
            return await axios.get(responseUri, { headers: { 'ignore-request-interceptor': true } });
          } catch (_error) {
            if ((_error as AxiosError).status === 403) {
              await delay(retryTimeoutSeconds * 1000);
            } else {
              throw _error;
            }
          }
        }

        return response;
      },
    });
  },
});
export { ContentTypes };

